import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';

const TAG = 'DoubleSidedSlider:';

/**
 * Slider increment precision in minutes.
 * Change this constant to adjust the granularity of the slider.
 */
export const SLIDER_INCREMENT_MINUTES = 10;

interface DoubleSidedSliderProps<T extends FieldValues> {
  leftFieldName: Path<T>;
  rightFieldName: Path<T>;
  control: Control<T>;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  totalValue?: number;
  leftLabelKey?: string;
  rightLabelKey?: string;
  onPercentageChange?: (leftPercentage: number, rightPercentage: number) => void;
}

/**
 * Generic dual-sided slider component for splitting a value between two categories.
 * Designed as a dual-sided progress bar with draggable thumb.
 *
 * Features:
 * - Interactive drag to adjust allocation percentages
 * - Real-time visual feedback with smooth animations
 * - React Hook Form integration for both fields
 * - Snaps to configurable minute increments for precision (see SLIDER_INCREMENT_MINUTES)
 * - Optional value-based time display (e.g., hours and minutes)
 * - Callback for percentage changes with float precision
 * - Disabled state support
 * - Accessible with keyboard navigation
 * - Fully internationalized labels
 *
 * @example
 * ```tsx
 * <DoubleSidedSlider
 *   leftFieldName="billableCapacity"
 *   rightFieldName="corporateFunctionCapacity"
 *   control={form.control}
 *   label="Time Allocation"
 *   description="Define how the user's work time is split between billable project work and corporate (non-billable) tasks."
 *   leftLabelKey="authenticated.team:details.tabs.capacity.billable"
 *   rightLabelKey="authenticated.team:details.tabs.capacity.corporateFunction"
 *   totalValue={8}
 *   onPercentageChange={(left, right) => console.log(`Left: ${left}%, Right: ${right}%`)}
 * />
 * ```
 */
export function DoubleSidedSlider<T extends FieldValues>({
  leftFieldName,
  rightFieldName,
  control,
  label,
  description,
  disabled = false,
  className,
  totalValue = 0,
  leftLabelKey,
  rightLabelKey,
  onPercentageChange,
}: DoubleSidedSliderProps<T>) {
  const { t } = useTranslation();
  const { field: leftField } = useController({ control, name: leftFieldName });
  const { field: rightField } = useController({
    control,
    name: rightFieldName,
  });

  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [visualPercentage, setVisualPercentage] = useState<number>(50);

  const leftPercentage = typeof leftField.value === 'number' ? leftField.value : 50;

  // Update visual percentage when left percentage changes
  useEffect(() => {
    setVisualPercentage(leftPercentage);
  }, [leftPercentage]);

  // On mount, snap the stored value to the nearest minute increment and write it
  // back so that form.getValues() matches what the slider displays. Without this,
  // API values like 63.33333 are stored raw but displayed as 64 (snapped), causing
  // a mismatch between the capacity tab and the conflict resolution modal.
  useEffect(() => {
    const snapped = roundToMinuteIncrement(leftPercentage);
    if (Math.abs(snapped - leftPercentage) > 1e-9) {
      leftField.onChange(snapped);
      rightField.onChange(parseFloat((100 - snapped).toFixed(5)));
    }
    // Run once on mount — roundToMinuteIncrement changes when totalValue changes,
    // but we don't want to re-snap on every totalValue edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Formats hours and minutes into a readable JSX element.
   * @param totalHours - Total hours as decimal (e.g., 2.25 for 2h 15m)
   * @returns Formatted string like "(2h 15m)" or "(2h)"
   */
  const formatHoursAndMinutes = useCallback((totalHours: number) => {
    let hours = Math.floor(totalHours);
    let minutes = Math.round((totalHours - hours) * 60);

    // Handle case where rounding gives us 60 minutes - convert to hours
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }

    if (minutes === 0) {
      return `(${i18n.t('common:timeFormat.hoursOnly', { hours })})`;
    }

    return `(${i18n.t('common:timeFormat.hoursAndMinutes', { hours, minutes })})`;
  }, []);

  /**
   * Rounds percentage to nearest value that represents the configured minute increments.
   * Uses SLIDER_INCREMENT_MINUTES constant for precision.
   */
  const roundToMinuteIncrement = useCallback(
    (percentage: number): number => {
      if (totalValue === 0) return Math.round(percentage);

      // Calculate how much percentage represents the configured minute increment
      const incrementPercentage = (SLIDER_INCREMENT_MINUTES / 60 / totalValue) * 100;

      // Round to nearest increment
      const rounded = Math.round(percentage / incrementPercentage) * incrementPercentage;

      // Round to reasonable precision (5 decimal places) to avoid floating point errors
      // This prevents values like 49.99999999999 while maintaining calculation precision
      return Math.round(rounded * 100000) / 100000;
    },
    [totalValue]
  );

  // Calculate display percentage with 5-minute snapping for display purposes
  const displayPercentage = useMemo(() => {
    return roundToMinuteIncrement(visualPercentage);
  }, [visualPercentage, roundToMinuteIncrement]);

  const updateValues = useCallback(
    (newLeftPercentage: number) => {
      const clamped = Math.max(0, Math.min(100, newLeftPercentage));
      const rounded = roundToMinuteIncrement(clamped);

      // Ensure the sum is always exactly 100% to prevent floating point errors
      const leftValue = rounded;
      const rightValue = 100 - rounded;

      // Verify that left + right = 100 (with floating point tolerance)
      const sum = leftValue + rightValue;
      if (Math.abs(sum - 100) > 0.0001) {
        logger.warn(TAG, 'Sum mismatch detected!', {
          left: leftValue,
          right: rightValue,
          sum,
        });
      }

      leftField.onChange(leftValue);
      rightField.onChange(rightValue);

      // Call callback with precise float values
      if (onPercentageChange) {
        onPercentageChange(leftValue, rightValue);
      }
    },
    [leftField, rightField, roundToMinuteIncrement, onPercentageChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;

      e.preventDefault();

      const track = trackRef.current;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;

      // Set visual percentage - will animate since isDragging is still false
      setVisualPercentage(Math.max(0, Math.min(100, percentage)));

      let isActuallyDragging = false;
      let latestPercentage = percentage; // Track the latest percentage

      // Attach listeners immediately to detect drag or click
      const handleMove = (moveEvent: MouseEvent) => {
        // First mousemove - user is dragging
        if (!isActuallyDragging) {
          isActuallyDragging = true;
          setIsDragging(true);
        }

        const trackElement = trackRef.current;
        if (!trackElement) return;

        const trackRect = trackElement.getBoundingClientRect();
        const moveX = moveEvent.clientX - trackRect.left;
        const movePercentage = (moveX / trackRect.width) * 100;

        latestPercentage = movePercentage; // Update latest percentage
        setVisualPercentage(Math.max(0, Math.min(100, movePercentage)));
      };

      const handleUp = () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);

        setIsDragging(false);

        // Use the latest percentage we have
        const snappedValue = roundToMinuteIncrement(latestPercentage);
        updateValues(snappedValue);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    },
    [disabled, roundToMinuteIncrement, updateValues]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      let newValue = leftPercentage;

      // Calculate step size for the configured minute increment
      const incrementPercentage =
        totalValue > 0 ? (SLIDER_INCREMENT_MINUTES / 60 / totalValue) * 100 : 1;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newValue = Math.max(0, leftPercentage - incrementPercentage);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newValue = Math.min(100, leftPercentage + incrementPercentage);
          break;
        case 'Home':
          e.preventDefault();
          newValue = 0;
          break;
        case 'End':
          e.preventDefault();
          newValue = 100;
          break;
        default:
          return;
      }

      updateValues(newValue);
    },
    [disabled, leftPercentage, updateValues, totalValue]
  );

  return (
    <div className={cn('flex flex-col gap-4 pb-0 pt-2', className)}>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium leading-none text-foreground">{label}</p>
        {description && (
          <p className="text-xs font-normal leading-4 text-muted-foreground">{description}</p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex gap-4 text-sm font-medium leading-none text-foreground">
          <p className="flex-1">{leftLabelKey ? t(leftLabelKey) : t('common:left')}</p>
          <p className="flex-1 text-right">
            {rightLabelKey ? t(rightLabelKey) : t('common:right')}
          </p>
        </div>

        <div
          ref={trackRef}
          className={cn(
            'relative h-2 w-full cursor-pointer rounded-full bg-secondary',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onMouseDown={handleMouseDown}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={leftPercentage}
          aria-label={label}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
        >
          {/* Left track */}
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-foreground"
            style={{
              width: `${visualPercentage}%`,
              transition: isDragging ? 'none' : 'width 150ms ease-out',
            }}
          />

          {/* Draggable thumb */}
          <div
            className={cn(
              'absolute top-1/2 size-5 -translate-y-1/2 rounded-full border-2 border-solid border-foreground bg-background',
              !disabled && 'cursor-grab active:cursor-grabbing',
              isDragging && 'ring-2 ring-ring ring-offset-2'
            )}
            style={{
              left: `${visualPercentage}%`,
              transform: 'translate(-50%, -50%)',
              transition: isDragging ? 'none' : 'left 150ms ease-out',
            }}
          />
        </div>

        <div className="flex gap-4 text-sm font-medium leading-none text-foreground">
          <p className="flex-1">
            {t('common:percent', { value: Math.round(displayPercentage) })}{' '}
            {totalValue > 0 && (
              <span className="text-muted-foreground">
                {formatHoursAndMinutes((displayPercentage / 100) * totalValue)}
              </span>
            )}
          </p>
          <p className="flex-1 text-right">
            {totalValue > 0 && (
              <span className="text-muted-foreground">
                {formatHoursAndMinutes(((100 - displayPercentage) / 100) * totalValue)}
              </span>
            )}{' '}
            {t('common:percent', { value: Math.round(100 - displayPercentage) })}
          </p>
        </div>
      </div>
    </div>
  );
}
